import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';

import {
  addLeadReferenceToWhatsAppUrl,
  createLeadReference,
  hasGoogleClickId,
  parseGoogleClickIds,
  type GoogleClickIds,
} from '@/lib/whatsapp-lead-tracking';

const storageKey = 'doq-google-click-ids';
const clickIdMaxAgeMs = 90 * 24 * 60 * 60 * 1000;
const whatsappClickConversion = 'AW-18389596213/zFiMCN2KzOEcELXw68BE';

type StoredClickIds = GoogleClickIds & { capturedAt: number };

function rememberCurrentClickIds(): GoogleClickIds {
  const current = parseGoogleClickIds(window.location.search);
  if (hasGoogleClickId(current)) {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ ...current, capturedAt: Date.now() }));
    } catch {
      // Tracking must never stop the enquiry link from working.
    }
    return current;
  }

  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) ?? 'null') as StoredClickIds | null;
    if (
      stored &&
      Number.isFinite(stored.capturedAt) &&
      Date.now() - stored.capturedAt <= clickIdMaxAgeMs &&
      hasGoogleClickId(stored)
    ) {
      return stored;
    }
    localStorage.removeItem(storageKey);
  } catch {
    // Ignore unavailable or malformed browser storage.
  }

  return {};
}

function openWhatsApp(url: string, target: string) {
  if (target === '_blank') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  window.location.assign(url);
}

function trackWhatsAppClick(transactionId: string) {
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  gtag?.('event', 'conversion', {
    send_to: whatsappClickConversion,
    value: 1,
    currency: 'HKD',
    transaction_id: transactionId,
  });
}

function recordSiteAnalytics(type: 'page_view' | 'whatsapp_click', path: string) {
  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      new Blob([JSON.stringify({ type, path })], { type: 'application/json' }),
    );
  } catch {
    // Analytics must never interrupt navigation or conversion tracking.
  }
}

export function WhatsAppLeadTracker() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    if (pathname !== '/analytics') recordSiteAnalytics('page_view', pathname);
  }, [pathname]);

  useEffect(() => {
    rememberCurrentClickIds();

    function handleWhatsAppClick(event: MouseEvent) {
      const anchor =
        event.target instanceof Element
          ? event.target.closest<HTMLAnchorElement>('a[href^="https://wa.me/"]')
          : null;
      if (!anchor) return;

      const reference = createLeadReference();
      trackWhatsAppClick(reference);
      recordSiteAnalytics('whatsapp_click', window.location.pathname);

      const clickIds = rememberCurrentClickIds();
      if (!hasGoogleClickId(clickIds)) return;

      const locale = window.location.pathname === '/en' || window.location.pathname.startsWith('/en/')
        ? 'en'
        : 'zh';
      const trackedUrl = addLeadReferenceToWhatsAppUrl(anchor.href, reference, locale);

      event.preventDefault();
      navigator.sendBeacon(
        '/api/whatsapp-leads',
        new Blob(
          [
            JSON.stringify({
              reference,
              ...clickIds,
              landingPage: window.location.pathname,
              clickedAt: new Date().toISOString(),
            }),
          ],
          { type: 'application/json' },
        ),
      );
      openWhatsApp(trackedUrl, anchor.target);
    }

    document.addEventListener('click', handleWhatsAppClick);
    return () => document.removeEventListener('click', handleWhatsAppClick);
  }, []);

  return null;
}
